import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { XIcon, MagnifyingGlassIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { fetchCoins, fetchCoin, trackEvent } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { verdictColor } from "./DegenScore";
import { fmtUsd, fmtAge, scoreColor } from "../lib/format";

const PILLARS = ["Volume Conviction", "Liquidity Depth", "Buy Pressure", "Survival", "Credibility", "Safety"];

const ScoreCol = ({ coin }) => {
  if (!coin) return null;
  const s = coin.score;
  const bd = Object.fromEntries((s.breakdown || []).map((b) => [b.key, b]));
  return (
    <div className="flex-1 min-w-0" data-testid={`compare-col-${coin.symbol}`}>
      <div className="text-center border-b border-white/10 pb-3 mb-3">
        <div className="font-heading font-black text-xl truncate">{coin.symbol}</div>
        <div className="font-mono text-2xl font-bold mt-1" style={{ color: scoreColor(s.total) }}>
          {s.total}<span className="text-xs text-muted-foreground">/{s.maxPossible || 100}</span>
        </div>
        <span className="font-mono text-[10px] px-2 py-0.5 border inline-block mt-1" style={{ color: verdictColor(s.verdict), borderColor: verdictColor(s.verdict) + "40" }}>{s.verdict}</span>
      </div>
      <div className="space-y-2">
        {PILLARS.map((p) => {
          const b = bd[p];
          const unknown = !b || b.value == null;
          return (
            <div key={p} className="text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground truncate mr-2">{p}</span>
                <span className="font-mono shrink-0" style={unknown ? { color: "#8A8F98" } : {}}>{unknown ? "?" : `${b.value}/${b.max}`}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5 text-xs">
        <div className="flex justify-between"><span className="text-muted-foreground">Mcap</span><span className="font-mono">{fmtUsd(coin.marketCap)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Liquidity</span><span className="font-mono">{fmtUsd(coin.liquidity)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">24h Vol</span><span className="font-mono">{fmtUsd(coin.volume?.h24)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Age</span><span className="font-mono">{fmtAge(coin.ageHours)}</span></div>
      </div>
    </div>
  );
};

export const CompareTokensModal = ({ coin, open, onClose }) => {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [otherAddr, setOtherAddr] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const searchQ = useQuery({
    queryKey: ["compareSearch", debounced, coin.chainId],
    queryFn: () => fetchCoins({ search: debounced, chain: coin.chainId || "solana" }),
    enabled: open && debounced.length >= 2,
  });
  const otherQ = useQuery({
    queryKey: ["compareCoin", otherAddr],
    queryFn: () => fetchCoin(otherAddr),
    enabled: !!otherAddr,
  });

  useEffect(() => { if (!open) { setQ(""); setOtherAddr(null); } }, [open]);
  if (!open) return null;

  const results = (searchQ.data?.coins || []).filter((c) => c.address !== coin.address).slice(0, 8);

  return createPortal(
    <div data-testid="compare-modal" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-white/15 max-w-lg w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Compare with another token</h3>
          <button data-testid="compare-close-btn" onClick={onClose} className="p-1.5 text-muted-foreground hover:text-white"><XIcon size={20} /></button>
        </div>

        {!otherAddr && (
          <>
            <div className="flex items-center gap-2 border border-white/15 px-3 py-2 focus-within:border-neon/50 transition-colors">
              <MagnifyingGlassIcon size={16} className="text-muted-foreground" />
              <input
                data-testid="compare-search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search ticker / name or paste a contract address"
                className="bg-transparent outline-none text-sm w-full"
              />
              {searchQ.isFetching && <SpinnerGapIcon size={16} className="animate-spin text-neon" />}
            </div>
            <div className="space-y-1.5 min-h-[80px]">
              {debounced.length < 2 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">Type at least 2 characters to search.</p>
              ) : results.length === 0 && !searchQ.isFetching ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No matches found.</p>
              ) : (
                results.map((c) => (
                  <button
                    key={c.address}
                    data-testid={`compare-result-${c.symbol}`}
                    onClick={() => { setOtherAddr(c.address); trackEvent("compare", getClientId(), { mint: coin.address, other: c.address }); }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-white/10 hover:border-neon/40 hover:bg-neon/[0.04] transition-colors text-left"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="font-heading font-bold text-sm">{c.symbol}</span>
                      <span className="text-xs text-muted-foreground truncate">{c.name}</span>
                    </span>
                    <span className="font-mono text-sm shrink-0" style={{ color: scoreColor(c.score.total) }}>{c.score.total}</span>
                  </button>
                ))
              )}
            </div>
          </>
        )}

        {otherAddr && (
          <>
            {otherQ.isLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground font-mono text-sm">
                <SpinnerGapIcon size={18} className="animate-spin text-neon" /> Loading…
              </div>
            ) : (
              <div className="flex gap-4">
                <ScoreCol coin={coin} />
                <div className="w-px bg-white/10 shrink-0" />
                <ScoreCol coin={otherQ.data} />
              </div>
            )}
            <button
              data-testid="compare-change-btn"
              onClick={() => setOtherAddr(null)}
              className="w-full px-4 py-2 border border-white/15 text-sm hover:border-neon/40 hover:text-neon transition-colors"
            >
              Pick a different token
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
